import { Card, IconButton, Skeleton, Stack, styled, Typography } from "~/common/mui";
import { C_PRIMARY_CONTAINER } from "~/common/components/icon/Colors";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { type MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  type AlternativeProductDTO,
  fetchAlternativeProducts,
} from "~/features/cart/cart/api/fetchAlternativeProducts";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { calculateSalesCampaign4PDP, useSelectPricingDetails } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { defaultSelectBestPackage, PSSizeMode } from "~/utils/cartHelper";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { ProductContext } from "~/domains/product/ProductContext";
import { Product } from "~/domains/product/Product";
import { useDispatch, useSelector } from "react-redux";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { fetchProductInfo } from "~/domains/product/api/fetchProductInfo";
import type { ProductData } from "~/domains/product/types/ProductData";
import { selectPackageEntryMap } from "~/features/cart/cart/redux/selectors/cartSelectors";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";
import { useAlternateProducts } from "~/features/cart/cart/AlternateProductsContext";
import { QuantityStockIndicator } from "~/domains/product/components/StockIndicator/StockIndicator";
import { ButtonSkeleton } from "~/common/components/CommonSkeletons";
import { renderButtons } from "~/features/saleRecommendations/hooks/useRecommendationTileButtons";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import {
  PurchasePlaceProvider,
  PurchaseTypeProvider,
  usePurchasePlace,
  usePurchaseType,
} from "~/app/analytics/ga/recommendations/RecommendationContexts";
import parseTruncate from "~/common/utils/parseTruncate";
import { getStore } from "~/app/store/getStore";
import { cartPageLoadThunk } from "~/features/cart/cart/redux/cartPageLoadThunk";
import { LinkType } from "~/common/components/Link";
import { sendEvent } from "~/app/analytics/ga/ga";

interface AlternateProductsProps {
  onClose: () => void;
  alternateProductsId: string;
  alternativesFor: string;
  onRender?: (element: Element) => void;
  orderEntryNumber: number;
}

interface AlternateProductProps {
  productCode: string;
  stockQuantity: number;
  orderEntryNumber: number;
  onClose: () => void;
}

const AlternateProductSkeleton = () => (
  <AlternateProductCard>
    <Skeleton variant="rounded" width={64} height={64} />
    <Stack gap={1}>
      <Typography variant="titleLarge" component={"b"}>
        <Skeleton width={200} />
      </Typography>
      <Typography>
        <Skeleton width={187} />
      </Typography>
      <ButtonSkeleton width={175} />
    </Stack>
  </AlternateProductCard>
);

const AlternateProduct = ({ productCode, stockQuantity, orderEntryNumber, onClose }: AlternateProductProps) => {
  const { setRecentlyAddedItem, setScrollIndex } = useAlternateProducts();
  const priceReference = getInitialPriceReference();
  const reco_type = usePurchaseType();
  const reco_place = usePurchasePlace();

  const productDescriptor = ProductService.useProductDescriptor({
    productCode: productCode,
    priceReference,
    options: { fetch: true },
  });
  const product = productDescriptor.base;

  const packaging = defaultSelectBestPackage(
    1,
    productDescriptor.availability?.packagingInformation?.map((p) => p.packCode) ?? [],
    productDescriptor,
    PSSizeMode.SMALLEST_FIRST,
  );
  const salesHelper = useSalesHelper(productDescriptor);
  const packCode = packaging?.packCode ?? INVALID_PACKAGE_CODE;
  const { data } = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor }), [productDescriptor]);
  const isOnSale = salesHelper.isPackageOnSale(packCode);

  const { price } = useSelectPricingDetails({
    packCode,
    quantity: 1,
    priceReference: getInitialPriceReference(),
    productCode: productCode,
    salesData: data?.salePerPack?.[packCode],
    saleActivated: isOnSale,
  });

  const packageEntryMap = useSelector(selectPackageEntryMap);

  function hasProductCode(productCode: string): boolean {
    return Object.values(packageEntryMap).some((product) => product.productCode === productCode);
  }

  function getProductIndexByCode(productCode: string): number {
    const entries = Object.values(packageEntryMap);
    return entries.findIndex((product) => product.productCode === productCode);
  }

  const onAddToCartClick = useMemo(() => {
    return async (e: MouseEvent<HTMLButtonElement>) => {
      const wizardConfig = {
        showSuccessDrawer: false,
        position: !hasProductCode(productCode) ? orderEntryNumber : undefined,
        onSuccessCallback: () => {
          getStore().dispatch(cartPageLoadThunk());
          onClose();
          if (hasProductCode(productCode)) {
            setRecentlyAddedItem(product);
            setScrollIndex(getProductIndexByCode(productCode));
          }
        },
      };

      await AddToCartService.openAddToCartWizard(
        {
          productCode: product.code,
          restrictionQuery: "",
          source: "fcc-addToCart",
          ...wizardConfig,
        },
        { page: reco_place, recommendationType: reco_type, recommendationPlace: reco_place },
      );

      sendEvent({
        event: "Ric_Related_Interaction",
        action: "Product Added To The Cart",
        raw_action: "Click",
        name: product.designation,
        id: product.code,
        is_reco_product: true,
        reco_place: reco_place,
        reco_type: reco_type,
      });
    };
  }, [product, productDescriptor]);

  const trackingAttributes = {
    "tracking-aux-ric-id": product.code,
    "tracking-aux-ric-name": product.designation,
  };
  const buttonTrackingAttributes = {
    "tracking-aux-ric-add": "true",
  };
  const buttonElement = renderButtons(productDescriptor, {
    atc: {
      onClick: onAddToCartClick,
      disabled: productDescriptor.isLoading || productDescriptor.isError || productDescriptor.isPriceLoading,
      fullWidth: false,
      size: "medium",
      color: "secondary",
      startIcon: <ShoppingCartOutlinedIcon />,
      ...buttonTrackingAttributes,
    },
  })[0];

  return (
    <ProductContext value={product}>
      <AlternateProductCard {...trackingAttributes}>
        <Product.PdpLink {...trackingAttributes}>
          <Product.PrimaryImageV2 size={60} />
        </Product.PdpLink>
        <Stack gap={1} alignItems={"flex-start"}>
          <Stack gap={0} direction="row" alignItems={"center"}>
            <Product.PreferredIndicator />
            <Product.PdpLink variant={LinkType.NAVIGATION} {...trackingAttributes}>
              <Product.DesignationV2 variant="titleLarge" component={"b"} />
            </Product.PdpLink>
          </Stack>
          <QuantityStockIndicator quantity={stockQuantity} />
          <PriceElements.Price price={price} variant="titleMedium" />
          {buttonElement}
        </Stack>
      </AlternateProductCard>
    </ProductContext>
  );
};

const AlternateProducts = ({
  onClose,
  alternateProductsId,
  alternativesFor,
  orderEntryNumber,
  onRender,
}: AlternateProductsProps) => {
  const [isLoading, setLoading] = useState(true);
  const [alternateProducts, setAlternateProducts] = useState<AlternativeProductDTO[]>([]);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onRender && rootRef.current) {
      window.setTimeout(() => {
        if (onRender && rootRef.current) {
          onRender(rootRef.current);
        }
      }, 16);
    }
  }, [onRender]);

  const fetchAlternativeProductData = async (productCodes: string[]): Promise<ProductData[]> => {
    const results = await Promise.all(productCodes.map((code) => fetchProductInfo(code, true).catch(() => null)));
    return results.filter((product): product is ProductData => product !== null);
  };

  const fetchAlternateProductsData = async () => {
    const data: { alternativeProducts: AlternativeProductDTO[] } = await fetchAlternativeProducts(alternateProductsId);
    setAlternateProducts(data.alternativeProducts);
    const codes = data.alternativeProducts.map((product) => product.code);
    fetchAlternativeProductData(codes)
      .then((products) => {
        if (products.length > 0) {
          products.forEach((product) => {
            dispatch(addBaseProductDataAction(product));
          });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAlternateProductsData();
  }, []);

  useEffect(() => {
    if (!isLoading && alternateProducts.length === 0) {
      onClose();
    }
  }, [isLoading, alternateProducts, onClose]);

  if (alternateProducts.length == 0 && !isLoading) {
    return null;
  }

  if (isLoading) {
    return (
      <Stack padding={3} gap={2} borderRadius={3} bgcolor={C_PRIMARY_CONTAINER} marginY={2} ref={rootRef}>
        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"flex-start"}>
          <Typography variant="headlineSmall">
            <Skeleton width={550} />
          </Typography>
          <IconButton variant="contained" color={"secondary"} size={"small"} onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
        <Stack direction={"row"} gap={3} sx={{ overflowX: "scroll", scrollbarWidth: "none" }}>
          {[...Array(4)].map((_, idx) => (
            <AlternateProductSkeleton key={idx} />
          ))}
        </Stack>
      </Stack>
    );
  }

  return (
    <PurchasePlaceProvider value={"CART"}>
      <PurchaseTypeProvider value={"ric"}>
        <Stack
          padding={3}
          gap={2}
          borderRadius={3}
          bgcolor={C_PRIMARY_CONTAINER}
          marginY={2}
          tracking-aux-ric-parent-product-name={alternativesFor}
          tracking-aux-ric-parent-product-id={alternateProductsId}
        >
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"flex-start"}>
            <Typography variant="headlineSmall">
              {parse(
                t("cartPage.alternate.title", {
                  alternativesFor: parseTruncate(alternativesFor, 50, "..."),
                }),
              )}
            </Typography>
            <IconButton variant="contained" color={"secondary"} size={"small"} onClick={onClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
          <AlternateProductsRow>
            {alternateProducts.map((product) => (
              <AlternateProduct
                key={product.code}
                productCode={product.code}
                stockQuantity={product.stockAmount}
                orderEntryNumber={orderEntryNumber}
                onClose={onClose}
              />
            ))}
          </AlternateProductsRow>
        </Stack>
      </PurchaseTypeProvider>
    </PurchasePlaceProvider>
  );
};

export default AlternateProducts;

const AlternateProductCard = styled(Card)({
  display: "flex",
  flex: "1 0 342px",
  alignItems: "flex-start",
  padding: "16px",
  gap: "16px",
  maxWidth: "418px",
});

const AlternateProductsRow = styled(Stack)({
  display: "flex",
  flexDirection: "row",
  gap: "24px",
  overflowX: "scroll",
  scrollbarWidth: "none",
});
